import { Element } from '@thive/canvas';
import { Areas, FloorplanRevision, LoadType } from '@thive/fddm-types';

import { elementFactory } from '@/modules/canvas/factories';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { AREAS_COLORS_MAP, modifyColor } from '@/modules/floorplanDesign/mappers/colors';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';

import { logicalSpaceMapper as mapLogicalSpaces } from '@/modules/canvas/mappers/logicalSpaceMapper';

import { createRack } from '@/modules/canvas/mappers/create3DRack';
import { createElevationRack } from '@/modules/canvas/mappers/create3DElevationRack';
import { createObstacle } from '@/modules/canvas/mappers/create3DObstacle';
import { createWall } from '@/modules/canvas/mappers/create3DWall';
import { createModelAreaRectangleElement } from './modelMapperHelpers';
import { segmentsToPathPoints } from './mapperHelpers';

export const modelMapper = (
  areas: Areas,
  obstacles: FloorplanRevision['design']['obstacles'],
  loadTypes: LoadType[],
  logicalSpaces: FloorplanRevision['design']['logicalSpaces'],
): Element[] => {
  const { stationaryAreas, highwayAreas, singularStationAreas, rackingAreas } = areas;
  const { elements: logicalSpaceElements } = mapLogicalSpaces(logicalSpaces);

  const stationaryAreaElements = stationaryAreas.map((stationaryArea) =>
    createModelAreaRectangleElement(
      `${stationaryArea.id}-rect`,
      stationaryArea.rectangle,
      Z_INDEX_MAP[FDDM_TAG.STATIONARY_AREA],
      modifyColor(AREAS_COLORS_MAP[stationaryArea.stationaryType], true),
    ),
  );

  const singularStationAreaElements = singularStationAreas.map((singularStationArea) =>
    createModelAreaRectangleElement(
      `${singularStationArea.id}-3d-rect`,
      singularStationArea.rectangle,
      Z_INDEX_MAP[FDDM_TAG.STATIONARY_AREA],
      modifyColor(AREAS_COLORS_MAP[singularStationArea.loadHandlingType], true),
    ),
  );

  const highwayElements: Element[] = highwayAreas.flatMap((area): Element[] => {
    const firstSegment = area.segments[0];
    if (!firstSegment) return [];

    if (area.segments.length === 1) {
      return [
        elementFactory.buildRectangleElement({
          id: `${area.id}-highway`,
          rectangle: RectangleUtils.fromSegment(firstSegment),
          z: Z_INDEX_MAP[FDDM_TAG.HIGHWAY_AREA],
          color: modifyColor(AREAS_COLORS_MAP.HIGHWAY, true),
          interactivity: INTERACTIVITY_OFF,
          tags: [FDDM_TAG.MODEL],
        }),
      ];
    }

    return [
      elementFactory.buildSegmentElement({
        id: `${area.id}-highway-segments`,
        points: segmentsToPathPoints(area.segments),
        z: Z_INDEX_MAP[FDDM_TAG.HIGHWAY_AREA],
        stroke: {
          color: modifyColor(AREAS_COLORS_MAP.HIGHWAY, true),
          width: firstSegment.leftSideWidth + firstSegment.rightSideWidth,
        },
        color: modifyColor(AREAS_COLORS_MAP.HIGHWAY, true),
        interactivity: INTERACTIVITY_OFF,
        tags: [FDDM_TAG.MODEL],
      }),
    ];
  });

  const rackElements: Element[] = rackingAreas.flatMap((rack) => createRack(rack, loadTypes));

  const elevationRackElements: Element[] = singularStationAreas.flatMap(
    (singularStationArea): Element[] => {
      if (singularStationArea.stationSpec.z <= 0) return [];

      const elevationRack = createElevationRack(singularStationArea, loadTypes);

      return elevationRack ? [elevationRack] : [];
    },
  );

  const obstacleElements: Element[] = obstacles.flatMap((obstacle): Element[] => {
    if (obstacle.appearance === 'WALL') return createWall(obstacle);

    return createObstacle(obstacle, obstacle.appearance);
  });

  return [
    ...logicalSpaceElements,
    ...stationaryAreaElements,
    ...singularStationAreaElements,
    ...highwayElements,
    ...rackElements,
    ...elevationRackElements,
    ...obstacleElements,
  ];
};
